import EndsBadge from "@/app/(custom-header)/prizes/car-v2/components/EndsBadge";
import { useContext, useMemo } from "react";
import { CarCompetitionData } from "@/app/(custom-header)/prizes/car-v2/contextProvider";
import { useAppDispatch } from "@/app/redux/hooks";
import { publishModalClasses, publishModalData, publishModalVisibility } from "@/app/redux/features/modal/modalSlice";
import EnterNowButton from "../EnterNowButton";
import { useIsMobile } from "@/app/redux/features/device/selectors";
import SpecsModalContent from "./SpecsModalContent";
import DOMPurify from "dompurify";

function splitSecondaryHtml(html: string) {
  const match = html.match(/<(h[1-4])[^>]*>[\s\S]*?<\/\1>/i);
  if (!match) {
    return { headingHtml: "", bodyHtml: html };
  }
  return {
    headingHtml: match[0],
    bodyHtml: html.replace(match[0], "").trim(),
  };
}

const secondaryHtmlClasses = `w-full font-jost text-dark-1 tracking-[0]
  [&_h3]:!mb-0 [&_h3]:text-2xl [&_h3]:lg:text-[32px] [&_h3]:font-black [&_h3]:leading-[30px] [&_h3]:lg:leading-[44px] [&_h3]:uppercase [&_h3]:text-dark-1 [&_h3]:lg:max-w-[573px]
  [&_h3]:text-center [&_h3]:lg:text-left
  [&_ul]:m-0 [&_ul]:mt-6 [&_ul]:lg:mt-8 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-3 [&_ul]:w-full [&_ul]:list-none [&_ul]:p-0
  [&_li]:flex [&_li]:items-start [&_li]:lg:items-center [&_li]:gap-2 [&_li]:lg:gap-3 [&_li]:text-gray-1 [&_li]:text-base [&_li]:lg:text-lg [&_li]:font-normal [&_li]:leading-5.5 [&_li]:lg:leading-6
  [&_li_svg]:w-2.5 [&_li_svg]:lg:w-5 [&_li_svg]:h-2 [&_li_svg]:lg:h-4 [&_li_svg]:shrink-0 [&_li_svg]:mt-2 [&_li_svg]:lg:mt-0
  [&_p]:mb-0 [&_p]:text-dark-1 [&_p]:text-base [&_p]:lg:text-lg [&_p]:leading-5.5 [&_p]:lg:leading-6 [&_p]:font-normal [&_p]:normal-case
  [&_p]:text-center [&_p]:lg:text-left`;

const CarSpecs = () => {
  const {
    carValue,
    cashAlternativeText,
    hideCashAlternative,
    competitionState,
    specsImage,
    secondarySectionHtml,
    secondarySectionCtaText,
  } = useContext(CarCompetitionData);

  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const showFinalHours = competitionState === "today" || competitionState === "last2Days";
  const { headingHtml, bodyHtml } = useMemo(
    () => splitSecondaryHtml(secondarySectionHtml || ""),
    [secondarySectionHtml]
  );

  const onShowAllSpecs = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    dispatch(publishModalData({ modalData: <SpecsModalContent /> }));
    dispatch(
      publishModalClasses({
        classNames:
          "rounded-2xl lg:w-[450px] max-lg:w-[calc(100%-theme(spacing.4))] max-lg:top-1/2 max-lg:-translate-y-1/2 max-lg:absolute",
      })
    );
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  return (
    <div className="flex flex-col items-stretch lg:items-center gap-6 lg:gap-10 self-stretch font-jost">
      {showFinalHours || headingHtml ? (
        <div className="flex lg:hidden flex-col items-center gap-2 px-4 w-full">
          {showFinalHours ? (
            <div className="bg-red-1 flex-center py-2 px-4 -skew-x-6">
              <span className="uppercase text-white text-center font-black text-2xl leading-6 skew-x-6">
                FINAL HOURS
              </span>
            </div>
          ) : null}
          {headingHtml ? (
            <div
              className={secondaryHtmlClasses}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(headingHtml) }}
            />
          ) : null}
        </div>
      ) : null}

      <div className="flex max-lg:flex-col items-stretch lg:items-center gap-6 lg:gap-10 self-stretch">
        <div className="relative w-full aspect-[1/1] lg:size-[620px] overflow-hidden rounded-none lg:rounded-2xl shrink-0">
          {specsImage ? (
            <>
              <EndsBadge className="top-6 left-6 right-auto" size="specs" />
              <div className="absolute inset-0 size-full bg-gradient-to-t from-black from-[0%] via-transparent via-[76%] to-transparent z-[1]" />
              <div className="flex max-lg:justify-center lg:justify-between items-center gap-6 absolute bottom-0 inset-x-0 pb-6 lg:pb-10 px-4 lg:px-[100px] z-[2]">
                <div className="flex flex-col items-center gap-2 max-lg:flex-1">
                  <span className="self-stretch text-white text-center text-sm lg:text-2xl font-normal lg:font-normal leading-4 lg:leading-[30px]">
                    Car Value
                  </span>
                  <span className="self-stretch text-white text-center text-xl lg:text-[28px] font-medium leading-5.5 lg:leading-8">
                    {carValue || "—"}
                  </span>
                </div>
                {!hideCashAlternative && (
                  <div className="flex flex-col items-center gap-2 max-lg:flex-1">
                    <span className="self-stretch text-white text-center text-sm lg:text-2xl font-normal leading-4 lg:leading-[30px]">
                      Cash Alternative
                    </span>
                    <span className="self-stretch text-white text-center text-xl lg:text-[28px] font-medium leading-5.5 lg:leading-8">
                      {cashAlternativeText || "—"}
                    </span>
                  </div>
                )}
              </div>
              <img
                src={specsImage}
                className="size-full object-cover aspect-[1/1]"
                alt="Car"
                width={620}
                height={620}
              />
            </>
          ) : (
            <div className="size-full bg-[#f1f1f1]" />
          )}
        </div>

        <div className="flex flex-col items-start max-lg:items-center gap-6 lg:gap-8 w-full max-lg:px-4">
          <div className="flex flex-col items-start max-lg:items-center gap-6 lg:gap-3 w-full">
            {!isMobile && showFinalHours ? (
              <div className="bg-red-1 flex-center py-2 px-6 -skew-x-6">
                <span className="uppercase text-white text-center font-black text-3.5xl leading-6 skew-x-6">
                  FINAL HOURS
                </span>
              </div>
            ) : null}

            {!isMobile && secondarySectionHtml ? (
              <div
                className={`${secondaryHtmlClasses} text-left`}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(secondarySectionHtml) }}
              />
            ) : null}

            {isMobile && bodyHtml ? (
              <div
                className={secondaryHtmlClasses}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bodyHtml) }}
              />
            ) : null}
          </div>

          <button
            onClick={onShowAllSpecs}
            className="m-0 p-0 border-0 self-start text-left text-[#6679B3] text-sm lg:text-base font-medium leading-5 lg:leading-5.5 uppercase cursor-pointer bg-transparent">
            {secondarySectionCtaText || "SHOW ALL SPECIFICATIONS"} »
          </button>

          <div className="max-lg:hidden">
            <EnterNowButton text="ENTER NOW »" variation="outline" place="car_new_second_cta" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarSpecs;
